import {DoctorField} from "@/pages/admin/DoctorPage/DoctorCreatePage";
import {ScenarioCriteriaView} from "@/pages/admin/ScenarioPage/components/ScenarioCriteriaView";
import CriteriaService from "@/services/CriteriaService";
import {viewDoctor} from "@/redux/slices/doctorSlices/viewSlice";
import {clearView, viewScenario} from "@/redux/slices/scenarioSlices/viewSlice";

import React, {useEffect, useRef, useState} from "react";

import {BackButton} from "@ui/buttons/BackButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {DocumentPreview} from "@ui/forms/DocumentPreview";
import {ViewField} from "@ui/forms/ViewField";
import {ViewTitle} from "@ui/forms/ViewTitle";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import {SCENARIO_CERTIFICATION_BADGES} from "@variables";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import styles from "./styles.module.scss";

export const ScenarioViewPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {id} = useParams();

  const scenarioRef = useRef();

  const {isMobile, isMayoly} = useSelector((state) => state.common.settings);
  const {current, status} = useSelector((state) => state.scenario.view);
  const viewDoctorState = useSelector((state) => state.doctor.view);

  const [templateName, setTemplateName] = useState("");

  const handleClickEdit = (id) => {
    navigate(`/admin-panel/call/edit/${id}`, {state: {id}});
  };

  useEffect(() => {
    dispatch(viewScenario({id}));

    return () => {
      dispatch(clearView());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (status !== "success" || !current?.doctor_profile_id) return;
    dispatch(viewDoctor({id: current.doctor_profile_id}));
  }, [dispatch, status, current?.doctor_profile_id]);

  useEffect(() => {
    if (!current?.evaluation_template_id) {
      setTemplateName("");
      return undefined;
    }

    let cancelled = false;

    CriteriaService.listAllCriteria().then((response) => {
      if (cancelled) {
        return;
      }

      const template = (response.data ?? []).find(
        (item) => String(item.id) === String(current.evaluation_template_id),
      );

      setTemplateName(template?.name ?? "");
    });

    return () => {
      cancelled = true;
    };
  }, [current?.evaluation_template_id]);

  useEffect(() => {
    if (scenarioRef.current && isMobile) {
      scenarioRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  return (
    <div
      ref={scenarioRef}
      className={styles.scenarioWrapper}
    >
      <BorderArticle>
        <div className={styles.scenario}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.scenarioContent}>
              <div className={styles.scenarioEditContent}>
                <div className={styles.scenarioContentHeadWrapper}>
                  <div className={styles.scenarioContentHead}>
                    <ViewTitle
                      value={current?.name}
                      handleClickEdit={() => handleClickEdit(current?.id)}
                    />

                    <ViewField
                      label={t("input.preview")}
                      direction={"col"}
                      customContent={
                        <PreviewCard
                          name={current?.name}
                          mask={current?.mask}
                          image={current?.cover}
                        />
                      }
                    />
                  </div>
                </div>
              </div>

              <ul
                className={`${styles.scenarioContentBody} ${styles.scenarioEditBody}`}
              >
                <ViewField
                  label={t("input.is_certification")}
                  customContent={
                    <RootCheckbox
                      checked={current?.certification}
                      disabled={false}
                      onChange={() => undefined}
                    />
                  }
                />

                {current?.certification && (
                  <ViewField
                    label={t("input.date_range")}
                    customContent={
                      current?.start_date && current?.finish_date ? (
                        <RootDesc>
                          <span className={"block p-[4rem_8rem]"}>
                            {t("input.select_date_from")}{" "}
                            <b style={{color: "var(--violet)"}}>
                              {moment(current?.start_date).format("DD-MM-YYYY")}
                            </b>{" "}
                            {t("input.select_date_to")}{" "}
                            <b style={{color: "var(--violet)"}}>
                              {moment(current?.finish_date).format("DD-MM-YYYY")}
                            </b>
                          </span>
                        </RootDesc>
                      ) : (
                        "-"
                      )
                    }
                  />
                )}

                <ViewField
                  label={t("input.period_of_action")}
                  value={moment(current?.due_date).format("DD-MM-YYYY")}
                />

                {!isMayoly && (
                  <ViewField
                    label={t("input.doctor")}
                    customContent={
                      current?.doctor_profile_id ? (
                        <div className={"flex items-center gap-[16rem]"}>
                          <RootDesc>
                            <span style={{padding: "4rem 8rem"}}>
                              {viewDoctorState.doctor?.id ==
                                current.doctor_profile_id &&
                                viewDoctorState.doctor?.name}
                            </span>
                          </RootDesc>
                          <DoctorField
                            doctorProfileId={current?.doctor_profile_id}
                          />
                        </div>
                      ) : (
                        <span style={{color: "var(--grey)"}}>-</span>
                      )
                    }
                  />
                )}

                <ViewField
                  label={t("input.related_products")}
                  value={
                    current?.products &&
                    current?.products
                      .map((product) => product?.name)
                      .filter(Boolean)
                      .join(", ")
                  }
                />

                <ViewField
                  label={t("input.purpose")}
                  value={current?.purpose}
                />

                <ViewField
                  label={t("input.objectives")}
                  value={current?.objectives}
                />

                <ViewField
                  label={t("input.messages")}
                  value={current?.key_messages}
                />

                <ViewField
                  label={t("input.words")}
                  value={current?.keywords}
                />

                <ViewField
                  label={t("input.structure")}
                  value={current?.structure}
                />

                <ViewField
                  label={t("input.script")}
                  value={current?.script}
                />

                <ViewField
                  label={t("input.objections")}
                  value={current?.questions}
                />

                <ViewField
                  label={t("input.materials")}
                  type={"custom"}
                  customContent={
                    current?.document_files &&
                    current?.document_files.length > 0
                      ? current?.document_files.map((file) => (
                          <React.Fragment key={file?.sha1}>
                            <DocumentPreview name={file?.file?.filename} />
                          </React.Fragment>
                        ))
                      : "-"
                  }
                />

                <ViewField
                  label={t("input.phase")}
                  value={
                    current?.phase === "primary"
                      ? t("input.primary")
                      : t("input.repeated")
                  }
                />

                <ViewField
                  label={t("input.criteria_name")}
                  value={templateName || "-"}
                />

                <ViewField
                  label={`${t("input.criteria")}`}
                  type={"custom"}
                  customContent={
                    <ScenarioCriteriaView
                      evaluationTemplateId={current?.evaluation_template_id}
                    />
                  }
                />

                {current?.certification && (
                  <ViewField
                    label={t("input.badge")}
                    value={current?.badge}
                    customContent={
                      <ul className={styles.badgeContent}>
                        <li style={{cursor: "default", pointerEvents: "none"}}>
                          <img
                            src={
                              SCENARIO_CERTIFICATION_BADGES.find(
                                (badge) => badge.name === current?.badge,
                              )?.image
                            }
                            alt={
                              SCENARIO_CERTIFICATION_BADGES.find(
                                (badge) => badge.name === current?.badge,
                              )?.name
                            }
                          />
                        </li>
                      </ul>
                    }
                  />
                )}
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
